import React, { useState, useEffect } from 'react';
import { X, Shirt, Search, Plus, ShoppingCart, Trash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { getGarmentTypes } from '@/pages/parametrizacion/garmentTypes/Services/garmentTypes.services';
import { getEdificioGarmentTypes } from '@/services/edificioGarmentTypes.service';
import { fetchEdificios } from '@/pages/parametrizacion/edificios/Services/edificios.services';

const SelectGarmentTypeModal = ({ isOpen, onClose, edificioId: propEdificioId, onSelect, onSave, showSaveButton = false }) => {
  const { toast } = useToast();
  const [garmentTypes, setGarmentTypes] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [observacionGeneral, setObservacionGeneral] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [edificios, setEdificios] = useState([]);
  const [selectedEdificio, setSelectedEdificio] = useState(null);
  const [isLoadingEdificios, setIsLoadingEdificios] = useState(false);
  const [accumulatedItems, setAccumulatedItems] = useState([]); // Items acumulados para guardar

  // Si no se pasa edificioId, usar el edificio seleccionado internamente
  const edificioId = propEdificioId || selectedEdificio?._id;

  useEffect(() => {
    if (isOpen && !propEdificioId) {
      loadEdificios();
    }
  }, [isOpen, propEdificioId]);

  useEffect(() => {
    if (isOpen && edificioId) {
      loadGarmentTypes();
    }
  }, [isOpen, edificioId]);
 
  const loadGarmentTypes = async () => {
    setIsLoading(true);
    try {
      // Obtener IDs de tipos de prenda asignados al edificio
      const assignedResponse = await getEdificioGarmentTypes(edificioId);
      const assignedIds = assignedResponse.data || [];

      if (assignedIds.length === 0) {
        toast({
          title: "Sin tipos de prenda",
          description: "Este portafolio no tiene tipos de prenda asignados",
          variant: "destructive",
        });
        setGarmentTypes([]);
        setIsLoading(false);
        return;
      }

      // Obtener todos los tipos de prenda
      const allGarmentTypesResponse = await getGarmentTypes({
        limit: 1000,
        offset: 1,
        query: { estado: 'Activo' }
      });

      // Filtrar solo los asignados al edificio
      const filteredGarmentTypes = (allGarmentTypesResponse.data || []).filter(gt =>
        assignedIds.includes(gt._id)
      );

      setGarmentTypes(filteredGarmentTypes);
    } catch (error) {
      console.error('Error al cargar tipos de prenda:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los tipos de prenda",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadEdificios = async () => {
    setIsLoadingEdificios(true);
    try {
      const response = await fetchEdificios({
        limit: 1000,
        offset: 1,
        query: { estado: 'Activo' }
      });
      setEdificios(response.data || []);
    } catch (error) {
      console.error('Error al cargar edificios:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los portafolios",
        variant: "destructive",
      });
    } finally {
      setIsLoadingEdificios(false);
    }
  };

  const handleToggleItem = (garmentType) => {
    setSelectedItems(prev => {
      const newSelected = { ...prev };
      if (newSelected[garmentType._id]) {
        delete newSelected[garmentType._id];
      } else {
        newSelected[garmentType._id] = {
          garmentType,
          cantidad: 1
        };
      }
      return newSelected;
    });
  };

  const handleCantidadChange = (garmentTypeId, cantidad) => {
    setSelectedItems(prev => ({
      ...prev,
      [garmentTypeId]: {
        ...prev[garmentTypeId],
        cantidad: parseInt(cantidad) || 1
      }
    }));
  };

  const handleAgregarTodos = () => {
    const selectedCount = Object.keys(selectedItems).length;

    if (selectedCount === 0) {
      toast({
        title: "Error",
        description: "Debe seleccionar al menos un tipo de prenda",
        variant: "destructive",
      });
      return;
    }

    // Validar cantidades
    const invalidItems = Object.values(selectedItems).filter(item => item.cantidad <= 0);
    if (invalidItems.length > 0) {
      toast({
        title: "Error",
        description: "Todas las cantidades deben ser mayores a 0",
        variant: "destructive",
      });
      return;
    }

    // Crear array de items seleccionados
    const itemsToAdd = Object.values(selectedItems).map(item => ({
      id: Date.now() + Math.random(),
      garmentTypeId: item.garmentType._id,
      nombre: item.garmentType.nombre,
      peso: item.garmentType.peso,
      cantidad: item.cantidad,
      observacion: observacionGeneral
    }));

    if (showSaveButton) {
      // Acumular items internamente para guardar después
      setAccumulatedItems(prev => [...prev, ...itemsToAdd]);

      // Limpiar selección actual para permitir agregar más
      setSelectedItems({});
      setObservacionGeneral('');

      toast({
        title: "Items agregados",
        description: `${itemsToAdd.length} item(s) agregado(s). Total: ${accumulatedItems.length + itemsToAdd.length}`,
      });
    } else {
      // Flujo original: llamar onSelect para cada item
      itemsToAdd.forEach(item => onSelect(item));

      // Reset form y cerrar
      setSelectedItems({});
      setObservacionGeneral('');
      onClose();
    }
  };

  const handleSave = () => {
    if (!propEdificioId && !selectedEdificio) {
      toast({
        title: "Error",
        description: "Debe seleccionar un portafolio",
        variant: "destructive",
      });
      return;
    }

    const selectedCount = Object.keys(selectedItems).length;
    
    // Si hay items seleccionados actualmente, agregarlos primero
    let finalItems = [...accumulatedItems];
    
    if (selectedCount > 0) {
      // Validar cantidades
      const invalidItems = Object.values(selectedItems).filter(item => item.cantidad <= 0);
      if (invalidItems.length > 0) {
        toast({
          title: "Error",
          description: "Todas las cantidades deben ser mayores a 0",
          variant: "destructive",
        });
        return;
      }

      // Agregar items seleccionados actuales
      const itemsToAdd = Object.values(selectedItems).map(item => ({
        id: Date.now() + Math.random(),
        garmentTypeId: item.garmentType._id,
        nombre: item.garmentType.nombre,
        peso: item.garmentType.peso,
        cantidad: item.cantidad,
        observacion: observacionGeneral
      }));
      
      finalItems = [...finalItems, ...itemsToAdd];
    }

    // Validar que haya al menos un item
    if (finalItems.length === 0) {
      toast({
        title: "Error",
        description: "Debe seleccionar al menos un item antes de guardar",
        variant: "destructive",
      });
      return;
    }

    if (onSave) {
      // Pasar el edificio y todos los items
      const edificioData = propEdificioId ? null : selectedEdificio;
      onSave(edificioData, finalItems);

      // Reset form completo
      setSelectedItems({});
      setObservacionGeneral('');
      setSelectedEdificio(null);
      setAccumulatedItems([]);
      onClose();
    }
  };

  const filteredGarmentTypes = garmentTypes.filter(gt =>
    gt.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  const selectedCount = Object.keys(selectedItems).length;
  const totalPeso = Object.values(selectedItems).reduce((sum, item) =>
    sum + (item.garmentType.peso * item.cantidad), 0
  );

  const handleRemoveItem = (garmentTypeId) => {
    setSelectedItems(prev => {
      const newSelected = { ...prev };
      delete newSelected[garmentTypeId];
      return newSelected;
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Shirt className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Seleccionar Tipos de Prenda
              </h2>
              <p className="text-sm text-gray-500">
                {selectedCount > 0 ? `${selectedCount} seleccionado(s) - Peso total: ${(totalPeso / 1000).toFixed(2)} kg` : 'Seleccione los tipos de prenda y Cantidades'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Main Content - Lista de tipos de prenda */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Selector de Portafolio (solo si no se pasa edificioId) */}
          {!propEdificioId && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Seleccione un Portafolio *
              </Label>
              {isLoadingEdificios ? (
                <p className="text-sm text-gray-500">Cargando portafolios...</p>
              ) : (
                <select
                  value={selectedEdificio?._id || ''}
                  onChange={(e) => {
                    const edificio = edificios.find(ed => ed._id === e.target.value);
                    setSelectedEdificio(edificio);
                    setSelectedItems({});
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- Seleccione un portafolio --</option>
                  {edificios.map((edificio) => (
                    <option key={edificio._id} value={edificio._id}>
                      {edificio.nombre} - {edificio.ciudad}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar tipos de prenda..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Cargando tipos de prenda...</p>
            </div>
          ) : filteredGarmentTypes.length === 0 ? (
            <div className="text-center py-8">
              <Shirt className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No se encontraron tipos de prenda parametrizados</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      <input
                        type="checkbox"
                        checked={selectedCount === filteredGarmentTypes.length && filteredGarmentTypes.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const allSelected = {};
                            filteredGarmentTypes.forEach(gt => {
                              allSelected[gt._id] = { garmentType: gt, cantidad: 1 };
                            });
                            setSelectedItems(allSelected);
                          } else {
                            setSelectedItems({});
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo de Prenda
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Peso (g)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                      Cantidad
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Peso Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredGarmentTypes.map((garmentType) => {
                    const isSelected = !!selectedItems[garmentType._id];
                    const cantidad = selectedItems[garmentType._id]?.cantidad || 1;
                    const pesoTotal = (garmentType.peso * cantidad / 1000).toFixed(2);

                    return (
                      <tr
                        key={garmentType._id}
                        className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleItem(garmentType)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}`}>
                              <Shirt className={`h-4 w-4 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{garmentType.nombre}</p>
                              {garmentType.descripcion && (
                                <p className="text-xs text-gray-500">{garmentType.descripcion}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <Badge variant="outline">{garmentType.peso}g</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            min="1"
                            value={cantidad}
                            onChange={(e) => handleCantidadChange(garmentType._id, e.target.value)}
                            disabled={!isSelected}
                            className="w-full"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {isSelected ? `${pesoTotal} kg` : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          </div>

          {/* Sidebar - Resumen de Solicitud */}
          <div className="w-96 border-l border-gray-200 bg-gray-50 overflow-y-auto">
            <div className="p-6 sticky top-0 bg-gray-50 z-10 border-b border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <ShoppingCart className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Resumen de Solicitud</h3>
              </div>
              <p className="text-xs text-gray-500">Items seleccionados para agregar</p>
            </div>

            <div className="p-6">
              {selectedCount === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="h-16 w-16 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 font-medium mb-1">
                    No hay prendas seleccionadas
                  </p>
                  <p className="text-xs text-gray-400">
                    Seleccione tipos de prenda de la lista
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Lista de items seleccionados */}
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {Object.values(selectedItems).map((item) => {
                      const pesoTotal = (item.garmentType.peso * item.cantidad / 1000).toFixed(2);
                      return (
                        <div key={item.garmentType._id} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <p className="font-medium text-sm text-gray-900">{item.garmentType.nombre}</p>
                              <p className="text-xs text-gray-500">
                                {item.garmentType.peso}g por unidad
                              </p>
                            </div>
                            <button
                              onClick={() => handleRemoveItem(item.garmentType._id)}
                              className="h-6 w-6 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                              title="Eliminar"
                            >
                              <Trash className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Cantidad:</span>
                            <span className="font-semibold text-gray-900">{item.cantidad} unidades</span>
                          </div>
                          <div className="flex items-center justify-between text-sm mt-1">
                            <span className="text-gray-600">Peso total:</span>
                            <span className="font-semibold text-blue-600">{pesoTotal} kg</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Totales */}
                  <div className="border-t border-gray-300 pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total de tipos:</span>
                      <span className="font-semibold text-gray-900">{selectedCount}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total de unidades:</span>
                      <span className="font-semibold text-gray-900">
                        {Object.values(selectedItems).reduce((sum, item) => sum + item.cantidad, 0)}
                      </span>
                    </div>
                    <div className="flex justify-between text-base font-bold border-t border-gray-300 pt-2">
                      <span className="text-gray-900">Peso total:</span>
                      <span className="text-blue-600">
                        {(totalPeso / 1000).toFixed(2)} kg
                      </span>
                    </div>
                  </div>

                  {/* Observación General */}
                  <div className="space-y-2 border-t border-gray-300 pt-4">
                    <Label htmlFor="observacion-general" className="text-sm font-medium text-gray-700">
                      Observación General
                    </Label>
                    <Textarea
                      id="observacion-general"
                      value={observacionGeneral}
                      onChange={(e) => setObservacionGeneral(e.target.value)}
                      placeholder="Observación para todos los items..."
                      rows={3}
                      className="text-sm"
                    />
                  </div>

                  {/* Acumulados (si existen) */}
                  {showSaveButton && accumulatedItems.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <ShoppingCart className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-900">
                          Items acumulados
                        </span>
                      </div>
                      <p className="text-xs text-blue-700">
                        {accumulatedItems.length} item(s) listo(s) para guardar
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {showSaveButton && accumulatedItems.length > 0 ? (
              <span className="font-medium text-blue-600">
                {accumulatedItems.length} item(s) listo(s) para guardar
              </span>
            ) : selectedCount > 0 ? (
              <span className="font-medium">
                {selectedCount} tipo(s) seleccionado(s) - Peso total: {(totalPeso / 1000).toFixed(2)} kg
              </span>
            ) : null}
          </div>
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancelar
            </Button>
            {!showSaveButton ? (
              <Button
                onClick={handleAgregarTodos}
                disabled={selectedCount === 0 || isLoading}
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar {selectedCount > 0 ? `(${selectedCount})` : ''}
              </Button>
            ) : (
              <Button
                onClick={handleSave}
                disabled={isLoading}
              >
                Guardar Solicitud
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectGarmentTypeModal;
